import * as React from 'react';
import { useState } from 'react';
import { EditorProps } from '../types/editor-props';
import {
  EditInvoiceLinesModal,
  InvoiceLinesValidationError,
  type InvoiceLineFormItem,
  type InvoiceLinesFormData
} from '../../../../modules/modals/EditInvoiceLinesModal';
import type { ValidationIssue } from '../../../../types';
import {
  useInvoiceIsUnitPriceInclVat,
  useInvoiceLineFields,
  useInvoiceRecomputeLine,
  useInvoiceVatRateOptions
} from '../../../../providers/InvoiceProvider';
import { InvoiceLinesFormatter } from '../formatters/InvoiceLinesFormatter';
import { rebaseInvoiceLineIssues } from '../../../forms/invoice-lines/utils';
import { toInvoiceLineItems } from '../utils/invoice-lines';

/**
 * Editor for structured invoice line item collections.
 *
 * Renders the current line summary inline and opens an EditInvoiceLinesModal.
 * On save, calls `onCommit` with a `{ invoiceLines, isUnitPriceInclVat }`
 * payload — the host's `onSave` should forward both into its invoice update
 * mutation atomically (one round-trip, no flag/lines drift).
 *
 * The invoice-level `isUnitPriceInclVat` is sourced from `InvoiceProvider`
 * context (host wires it from the loaded invoice); the modal lets the user
 * toggle it and the new value comes back in the save payload.
 */
export function InvoiceLinesEditor({
  value,
  onChange,
  onCommit,
  onCancel,
  errors: externalErrors
}: EditorProps) {
  const [open, setOpen] = useState(true);
  // Seed from props so external (host-supplied) issues are visible when the
  // modal first opens; failed saves overwrite this with server-side issues.
  const [errors, setErrors] = useState<ValidationIssue[]>(() =>
    rebaseInvoiceLineIssues(externalErrors ?? [])
  );
  // VAT rate presets are scoped per invoice (supplier country + tax date) by
  // the host app via InvoiceProvider. Falling back to [] is acceptable
  // — VatRateSelector then degrades to its freeform `%` input.
  const vatRateOptions = useInvoiceVatRateOptions();
  const isUnitPriceInclVat = useInvoiceIsUnitPriceInclVat();
  // Host-supplied via InvoiceProvider; undefined means no live backend
  // recompute (the modal then renders without total-mismatch warnings).
  const onRecomputeLine = useInvoiceRecomputeLine();
  // Host fields per line (e.g. a code from the host's own list); none outside a provider.
  const lineFields = useInvoiceLineFields();

  // Tolerant of both shapes: the host's line array, and the committed
  // `{ invoiceLines, isUnitPriceInclVat }` payload EditableField holds mid-save.
  const lines: InvoiceLineFormItem[] = toInvoiceLineItems(value);

  const handleSave = async (data: InvoiceLinesFormData) => {
    try {
      const payload = {
        invoiceLines: data.invoiceLines,
        isUnitPriceInclVat: data.isUnitPriceInclVat
      };
      if (onCommit) {
        // Awaiting lets the modal catch save errors and keep itself open with a
        // banner instead of closing on a failed save.
        await onCommit(payload);
      } else {
        onChange(payload);
      }
      setErrors([]);
    } catch (error) {
      // Server-side line validation errors travel as InvoiceLinesValidationError;
      // surface their issues on the modal so the user can correct them in place.
      if (error instanceof InvoiceLinesValidationError) {
        setErrors(error.issues);
      }
      throw error;
    }
  };

  const handleClose = () => {
    setOpen(false);
    // Modal-based editors must surface dismissal up to EditableField, otherwise
    // the field stays in edit mode and the formatter underneath can't reopen
    // the modal on the next click.
    onCancel?.();
  };

  return (
    <>
      <div className='w-full'>
        <InvoiceLinesFormatter value={lines} />
      </div>
      <EditInvoiceLinesModal
        open={open}
        currentLines={lines}
        currentIsUnitPriceInclVat={isUnitPriceInclVat}
        vatRateOptions={vatRateOptions}
        errors={errors}
        onClose={handleClose}
        onSave={handleSave}
        onRecomputeLine={onRecomputeLine}
        lineFields={lineFields}
      />
    </>
  );
}

(InvoiceLinesEditor as React.FC).displayName = 'InvoiceLinesEditor';
